import { Component, OnInit } from '@angular/core';
import { MoreButtonComponent } from '../../../core/buttons/more-button.component';
import { TagComponent, TagType } from '../../../core/labels/tag.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { BaseComponent } from '../../../base/base.component';
import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';
import { ClaimAUClaimCard } from '../../../../models/api/view/claim/summary/claim-au-claim-card';
import {
  LoadingPanelComponent,
  LoadingState,
} from '../../../core/panels/loading-panel.component';
import { ClaimFlagsComponent } from './claim-flags.component';
import { ClaimLabelComponent } from '../../../core/labels/claim-label.component';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';
import { DateEx } from '../../../../functions/date-ex';
import { getFieldVisibility } from '../../../../functions/field-security';

@Component({
  selector: 'app-au-claim-card',
  imports: [
    DetailPanelComponent,
    DetailItemComponent,
    TagComponent,
    ActionRowComponent,
    MoreButtonComponent,
    LoadingPanelComponent,
    ClaimFlagsComponent,
    ClaimLabelComponent,
  ],
  template: `
    <app-loading-panel
      [state]="loadingState"
      [loadingHeight]="300"
      loadingStyle="Box"
      [error]="error"
    >
      <ng-template #loaded>
        @if (auClaimCard) {
          <div class="tw-w-full">
            <div
              class="tw-flex tw-flex-col md:tw-flex-row tw-gap-0 md:tw-gap-8 tw-w-full"
            >
              <!-- Column 1 -->
              <app-detail-panel
                [dataSource]="auClaimCard"
                class="tw-w-full"
                ariaLabel="Claim information"
              >
                <app-detail-item
                  label="Claim number"
                  [ariaLabel]="'Claim Number: ' + auClaimCard?.claimNumber"
                >
                  <ng-template let-data #value>
                    <app-claim-label [claim]="claimRow" [readOnly]="true" />
                  </ng-template>
                </app-detail-item>
                <app-detail-item
                  label="Status"
                  [ariaLabel]="'Status: ' + statusDisplay"
                >
                  <ng-template let-data #value>
                    <app-tag [type]="statusType">{{ statusDisplay }}</app-tag>
                  </ng-template>
                </app-detail-item>
                <app-detail-item label="Date of loss">
                  <ng-template let-data #value>
                    {{
                      DateEx.formatDateWithTimeCode(
                        data.dateOfLoss,
                        data.eventTime
                      )
                    }}
                  </ng-template>
                </app-detail-item>
                <app-detail-item label="Jurisdiction" name="jurisdiction" />
              </app-detail-panel>

              <!-- Column 2 -->
              <app-detail-panel
                [dataSource]="injuryData"
                class="tw-w-full"
                ariaLabel="Injury information"
              >
                @if (visibility?.showCause) {
                  <app-detail-item label="Cause" name="cause" />
                }
                @if (visibility?.showNatureResult) {
                  <app-detail-item
                    label="Nature/Result"
                    name="nature"
                    labelCase="none"
                  />
                }
                @if (visibility?.showPartTarget) {
                  <app-detail-item
                    label="Part/Target"
                    name="part"
                    labelCase="none"
                  />
                }
                @if (showPointOfImpact) {
                  <app-detail-item
                    label="Point of impact"
                    name="pointOfImpact"
                  />
                }
                @if (showBodySide) {
                  <app-detail-item
                    label="Body side"
                    name="bodySide"
                    labelCase="none"
                  />
                }
              </app-detail-panel>
            </div>

            <!-- Event Description -->
            @if (
              visibility?.showEventDescription && auClaimCard.eventDescription
            ) {
              <div role="region" aria-label="Event description">
                <div class="tw-font-bold tw-py-2" id="event-description-label">
                  Event description
                </div>
                <div
                  class="tw-pb-4 tw-whitespace-pre-line"
                  tabindex="0"
                  role="article"
                  aria-labelledby="event-description-label"
                >
                  {{ auClaimCard.eventDescription }}
                </div>
              </div>
            }

            <!-- Claim Flags and More Button -->
            <app-action-row>
              <app-claim-flags left1 [flags]="auClaimCard" />
              @if (detailAuth) {
                <app-more-button
                  right1
                  (click)="this.view.navigateCurrentClaim(['auto-liability'])"
                  [ariaLabel]="
                    'Navigate to Auto Liability Details for ' +
                    auClaimCard?.claimNumber
                  "
                />
              }
            </app-action-row>
          </div>
        }
      </ng-template>
    </app-loading-panel>
  `,
  styles: ``,
})
export class AuClaimCardComponent extends BaseComponent implements OnInit {
  protected DateEx = DateEx;
  claimRow: ClaimRow;
  auClaimCard: ClaimAUClaimCard | null = null;
  loadingState: LoadingState = 'Loading';
  error: any = null;
  detailAuth = false;
  statusType: TagType = 'Success';
  statusDisplay = '';
  injuryData: any = {};
  showBodySide = false;
  showPointOfImpact = false;
  visibility: ReturnType<typeof getFieldVisibility>;

  @HandleLoadingState({
    loadingProperty: 'loadingState',
    onValue: 'Loading',
    offValue: 'Loaded',
  })
  async ngOnInit(): Promise<void> {
    this.claimRow = this.view.currentClaim();
    this.detailAuth = this.view.authorize('Details');

    this.auClaimCard = await this.api.claimSummary.getAUClaimCard(
      this.claimRow.recordSource,
      this.claimRow.recordId,
    );

    this.statusDisplay =
      this.auClaimCard.status +
      (this.auClaimCard.subStatus ? ' - ' + this.auClaimCard.subStatus : '');
    this.statusType = this.getStatusType(this.auClaimCard.status);

    // Body Side only displays if Coverage Code is BI or MP
    const coverageCode = this.auClaimCard.coverageCode;
    this.showBodySide = coverageCode === 'BI' || coverageCode === 'MP';

    // Point of Impact only displays if Coverage Code is CL, CM, or PD
    this.showPointOfImpact =
      coverageCode === 'CL' || coverageCode === 'CM' || coverageCode === 'PD';

    this.visibility = getFieldVisibility(
      this.session.user?.fieldSecurity?.restrictedFields,
    );

    this.injuryData = {
      cause: this.auClaimCard.cause || '',
      nature: this.auClaimCard.natureResult || '',
      part: this.auClaimCard.partTarget || '',
      pointOfImpact: this.auClaimCard.pointOfImpact || '',
      bodySide: this.auClaimCard.bodySide || '',
    };
  }

  private getStatusType(status: string): TagType {
    const statusUpper = status?.toUpperCase() || '';
    if (statusUpper.includes('OPEN') || statusUpper.includes('REOPEN')) {
      return 'Success';
    }
    if (statusUpper.includes('CLOSED')) {
      return 'Error';
    }
    if (statusUpper.includes('INCIDENT')) {
      return 'Warn';
    }
    return 'Success';
  }
}
